/**
 * Shared Utilities
 * Common utility functions following TypeScript best practices
 */


import { REGEX_PATTERNS, DATE_FORMATS } from '../constants';
import { LogLevel } from '../types';
import { CookieManager } from './cookies';
import { ENV } from '@/config/environment';

// Raw fetch, not apiService - apiService imports `logger` from this file,
// so importing apiService back here would be a circular dependency. This
// is also more robust as a fire-and-forget beacon: it still works if
// apiService itself is what's broken.
let reportedThisSession = 0;
const MAX_CLIENT_ERROR_REPORTS_PER_SESSION = 20; // guard against error-loops flooding the backend

function reportErrorToBackend(message: string, metadata?: Record<string, unknown>) {
  if (!ENV.features.errorReporting || !ENV.project.id) return;
  if (reportedThisSession >= MAX_CLIENT_ERROR_REPORTS_PER_SESSION) return;
  reportedThisSession += 1;

  const stack = metadata?.error instanceof Error ? metadata.error.stack : undefined;

  fetch(`${ENV.api.baseUrl}/errors`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Project-Id': ENV.project.id,
    },
    body: JSON.stringify({
      message: message.slice(0, 500),
      stack: stack?.slice(0, 4000),
      url: window.location.href.slice(0, 500),
      user_agent: navigator.userAgent.slice(0, 300),
      level: 'error',
    }),
    keepalive: true,
  }).catch(() => {
    // Reporting failures must never surface to the user or recurse into
    // more logging - swallow silently.
  });
}

// === String Utilities ===
export const stringUtils = {
  /**
   * Capitalize first letter of a string
   */
  capitalize: (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  },

  /**
   * Convert string to title case
   */
  toTitleCase: (str: string): string => {
    return str.replace(/\w\S*/g, (txt) => 
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  },

  /**
   * Create a URL-friendly slug from a string
   */
  slugify: (str: string): string => {
    return str
      .toLowerCase()
      .replace(/[^\w ]+/g, '')
      .replace(/ +/g, '-')
      .trim();
  },

  /**
   * Truncate string with ellipsis
   */
  truncate: (str: string, length: number): string => {
    return str.length <= length ? str : `${str.slice(0, length)}...`;
  },

  /**
   * Generate a random string
   */
  randomString: (length: number = 8): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  },
} as const;

// === Date Utilities ===
export const dateUtils = {
  /**
   * Format date to string
   */
  format: (date: Date | string, format: keyof typeof DATE_FORMATS = 'SHORT'): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Simple formatting - in a real app, use date-fns or similar
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: format === 'LONG' ? 'long' : 'short',
      day: 'numeric',
    };
    
    if (format === 'WITH_TIME') {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }
    
    return new Intl.DateTimeFormat('en-US', options).format(dateObj);
  },

  /**
   * Get relative time (e.g., "2 hours ago")
   */
  timeAgo: (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
    };

    for (const [unit, seconds] of Object.entries(intervals)) {
      const interval = Math.floor(diffInSeconds / seconds);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }

    return 'Just now';
  },

  /**
   * Check if date is today
   */
  isToday: (date: Date | string): boolean => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const today = new Date();
    return dateObj.toDateString() === today.toDateString();
  },

  /**
   * Add days to a date
   */
  addDays: (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  },
} as const;

// === Validation Utilities ===
export const validationUtils = {
  /**
   * Validate email address
   */
  isValidEmail: (email: string): boolean => {
    return REGEX_PATTERNS.EMAIL.test(email);
  },

  /**
   * Validate password strength
   */
  isValidPassword: (password: string): boolean => {
    return password.length >= 8 && REGEX_PATTERNS.PASSWORD.test(password);
  },

  /**
   * Validate URL
   */
  isValidUrl: (url: string): boolean => {
    return REGEX_PATTERNS.URL.test(url);
  },

  /**
   * Validate username
   */
  isValidUsername: (username: string): boolean => {
    return username.length >= 3 && REGEX_PATTERNS.USERNAME.test(username);
  },

  /**
   * Check if value is empty
   */
  isEmpty: (value: unknown): boolean => {
    return value == null || value === '' || (Array.isArray(value) && value.length === 0);
  },
} as const;

// === Array Utilities ===
export const arrayUtils = {
  /**
   * Remove duplicates from array
   */
  unique: <T>(array: T[]): T[] => {
    return [...new Set(array)];
  },

  /**
   * Group array by key
   */
  groupBy: <T, K extends keyof T>(array: T[], key: K): Record<string, T[]> => {
    return array.reduce((groups, item) => {
      const group = String(item[key]);
      groups[group] = groups[group] || [];
      groups[group].push(item);
      return groups;
    }, {} as Record<string, T[]>);
  },

  /**
   * Chunk array into smaller arrays
   */
  chunk: <T>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  },

  /**
   * Shuffle array
   */
  shuffle: <T>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  },
} as const;

// === Object Utilities ===
export const objectUtils = {
  /**
   * Deep clone an object
   */
  deepClone: <T>(obj: T): T => {
    return JSON.parse(JSON.stringify(obj));
  },

  /**
   * Check if object is empty
   */
  isEmpty: (obj: object): boolean => {
    return Object.keys(obj).length === 0;
  },

  /**
   * Pick specific keys from object
   */
  pick: <T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> => {
    const result = {} as Pick<T, K>;
    keys.forEach(key => {
      if (key in obj) {
        result[key] = obj[key];
      }
    });
    return result;
  },

  /**
   * Omit specific keys from object
   */
  omit: <T, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
    const result = { ...obj };
    keys.forEach(key => {
      delete result[key];
    });
    return result;
  },
} as const;

// === Cookie Storage Utilities ===
export const storageUtils = {
  /**
   * Set item in cookies with error handling
   */
  setItem: (key: string, value: string | number | boolean): boolean => {
    try {
      CookieManager.set(key, String(value), {
        expires: 30, // 30 days default
        path: '/',
        secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
        sameSite: 'lax'
      });
      return true;
    } catch {
      // Silently fail - cookies might be unavailable
      return false;
    }
  },

  /**
   * Get item from cookies with error handling
   */
  getItem: (key: string, defaultValue?: string): string | null => {
    try {
      const item = CookieManager.get(key);
      return item ? item : null;
    } catch (error) { 
      // store exact error message for debugging
      console.error('Error retrieving cookie item:', error);
      return defaultValue || null;
    }
  },

  /**
   * Remove item from cookies
   */
  removeItem: (key: string): boolean => {
    try {
      CookieManager.remove(key, { path: '/' });
      return true;
    } catch {
      // Silently fail - cookies might be unavailable
      return false;
    }
  },

  /**
   * Clear all cookies (note: this only clears cookies set by this app)
   */
  clear: (): boolean => {
    try {
      // Note: We can't clear all cookies, only remove specific ones
      // This is a limitation of cookies vs localStorage
      logger.warn('storageUtils.clear() called - cannot clear all cookies like localStorage', 'StorageUtils');
      return true;
    } catch {
      // Silently fail - cookies might be unavailable
      return false;
    }
  },
} as const;

// === URL Utilities ===
export const urlUtils = {
  /**
   * Build URL with query parameters
   */
  buildUrl: (baseUrl: string, params: Record<string, unknown>): string => {
    const url = new URL(baseUrl, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      if (value != null) {
        url.searchParams.set(key, String(value));
      }
    });
    return url.toString();
  },

  /**
   * Parse query parameters from URL
   */
  parseQuery: (search: string = window.location.search): Record<string, string> => {
    const params = new URLSearchParams(search);
    const result: Record<string, string> = {};
    params.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  },

  /**
   * Get file extension from URL
   */
  getFileExtension: (url: string): string => {
    return url.split('.').pop()?.toLowerCase() || '';
  },
} as const;

// === Number Utilities ===
export const numberUtils = {
  /**
   * Format number as currency
   */
  formatCurrency: (amount: number, currency: string = 'USD'): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  },

  /**
   * Format number with commas
   */
  formatNumber: (num: number): string => {
    return new Intl.NumberFormat('en-US').format(num);
  },

  /**
   * Round to specific decimal places
   */
  round: (num: number, decimals: number = 2): number => {
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  },

  /**
   * Clamp number between min and max
   */
  clamp: (num: number, min: number, max: number): number => {
    return Math.min(Math.max(num, min), max);
  },
} as const;

// === Async Utilities ===
export const asyncUtils = {
  /**
   * Delay execution for specified milliseconds
   */
  delay: (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
  },

  /**
   * Retry async function with exponential backoff
   */
  retry: async <T>(
    fn: () => Promise<T>,
    attempts: number = 3,
    delay: number = 1000
  ): Promise<T> => {
    try {
      return await fn();
    } catch (error) {
      if (attempts <= 1) throw error;
      await asyncUtils.delay(delay);
      return asyncUtils.retry(fn, attempts - 1, delay * 2);
    }
  },

  /**
   * Debounce function calls
   */
  debounce: <T extends (...args: never[]) => unknown>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  },

  /**
   * Throttle function calls
   */
  throttle: <T extends (...args: never[]) => unknown>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void => {
    let inThrottle: boolean;
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },
} as const;

// === Error Utilities ===
export const errorUtils = {
  /**
   * Extract error message from unknown error
   */
  getErrorMessage: (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'An unknown error occurred';
  },

  /**
   * Check if error is network error
   */
  isNetworkError: (error: unknown): boolean => {
    return error instanceof Error && 
           (error.message.includes('Network') || 
            error.message.includes('fetch'));
  },
} as const;

// === Logger Utility ===
// Check if we're in a non-production environment
const isNonProduction = (): boolean => {
  // Check multiple ways to determine if we're in development/staging
  const env = import.meta.env.MODE || import.meta.env.NODE_ENV || 'development';
  const isDev = import.meta.env.DEV || env === 'development' || env === 'staging' || env === 'test';
  return isDev;
};

export const logger = {
  /**
   * Log entry - only logs in non-production environments
   */
  log: (level: LogLevel, message: string, category?: string, metadata?: Record<string, unknown>): void => {
    // Only log in non-production environments
    if (!isNonProduction() && level !== LogLevel.ERROR) {
      return; // Skip non-error logs in production
    }

    // Create log entry
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${level.toUpperCase()}] ${category ? `[${category}] ` : ''}${message}`;
    
    // Format metadata for better readability
    const formattedMetadata = metadata ? JSON.stringify(metadata, null, 2) : '';
    
    switch (level) {
      case LogLevel.DEBUG:
        if (isNonProduction()) {
          console.debug(logMessage, formattedMetadata ? JSON.parse(formattedMetadata) : '');
        }
        break;
      case LogLevel.INFO:
        if (isNonProduction()) {
          console.info(logMessage, formattedMetadata ? JSON.parse(formattedMetadata) : '');
        }
        break;
      case LogLevel.WARN:
        if (isNonProduction()) {
          console.warn(logMessage, formattedMetadata ? JSON.parse(formattedMetadata) : '');
        }
        break;
      case LogLevel.ERROR:
        // Always log errors, even in production (but with less detail)
        if (isNonProduction()) {
          console.error(logMessage, formattedMetadata ? JSON.parse(formattedMetadata) : '');
        } else {
          // In production, log minimal error info
          console.error(`[ERROR] ${category ? `[${category}] ` : ''}${message}`);
        }
        break;
    }

    // Ship production errors to the backend so they show up in the admin
    // Error Monitoring screen instead of only ever appearing in a user's
    // own browser console.
    if (import.meta.env.PROD && level === LogLevel.ERROR) {
      reportErrorToBackend(message, metadata);
    }
  },

  debug: (message: string, category?: string, metadata?: Record<string, unknown>) => 
    logger.log(LogLevel.DEBUG, message, category, metadata),

  info: (message: string, category?: string, metadata?: Record<string, unknown>) => 
    logger.log(LogLevel.INFO, message, category, metadata),

  warn: (message: string, category?: string, metadata?: Record<string, unknown>) => 
    logger.log(LogLevel.WARN, message, category, metadata),

  error: (message: string, category?: string, metadata?: Record<string, unknown>) => 
    logger.log(LogLevel.ERROR, message, category, metadata),
} as const;

// Export cookies utilities
export { CookieManager, ThemeCookieManager } from './cookies';

export { prepareHtmlForEditor, sanitizeBlogHtml } from './blogContentUtils';

// Export all utilities
export default {
  stringUtils,
  dateUtils,
  validationUtils,
  arrayUtils,
  objectUtils,
  storageUtils,
  urlUtils,
  numberUtils,
  asyncUtils,
  errorUtils,
  logger,
} as const;
